import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ItemType, UploadType } from 'api/Responses/BulkUploadResponses';
import { RootState } from 'store';

export interface DataUploadSliceState {
  fileName: string;
  checkState: {
    unique: ItemType[];
    update: ItemType[];
    ignore: ItemType[];
  };
}

const initialState: DataUploadSliceState = {
  fileName: '',
  checkState: {
    unique: [],
    update: [],
    ignore: [],
  },
};

const dataUploadSlice = createSlice({
  name: 'dataUpload',
  initialState,
  reducers: {
    updateData: (state, action) => {
      state.fileName = action.payload.upload_name;
      if (action.payload.upload_type === UploadType['Set Upload']) {
        const itemsWithUUIDsUnique = action.payload.sets.unique.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: true,
        }));
        state.checkState.unique = itemsWithUUIDsUnique;

        const itemsWithUUIDsIgnore = action.payload.sets.ignore.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
        }));
        state.checkState.ignore = itemsWithUUIDsIgnore;

        const itemsWithUUIDsUpdate = action.payload.sets.update.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          updateItem: true,
        }));
        state.checkState.update = itemsWithUUIDsUpdate;
      } else if (action.payload.upload_type === UploadType['Device Upload']) {
        const itemsWithUUIDsUnique = action.payload.parts.unique.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: true,
        }));
        state.checkState.unique = itemsWithUUIDsUnique;

        const itemsWithUUIDsUpdate = action.payload.parts.update.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          updateItem: true,
        }));
        state.checkState.update = itemsWithUUIDsUpdate;

        const itemsWithUUIDsIgnore = action.payload.parts.ignore.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
        }));
        state.checkState.ignore = itemsWithUUIDsIgnore;
      } else if (action.payload.upload_type === UploadType['Device Attachments']) {
        const itemsWithUUIDsUnique = action.payload.part_attachments.unique.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: true,
        }));
        state.checkState.unique = itemsWithUUIDsUnique;

        const itemsWithUUIDsUpdate = action.payload.part_attachments.update.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          updateItem: true,
        }));
        state.checkState.update = itemsWithUUIDsUpdate;

        const itemsWithUUIDsIgnore = action.payload.part_attachments.ignore.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
        }));
        state.checkState.ignore = itemsWithUUIDsIgnore;
      } else if (action.payload.upload_type === UploadType['Set Attachments']) {
        const itemsWithUUIDsUnique = action.payload.set_attachments.unique.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: true,
        }));
        state.checkState.unique = itemsWithUUIDsUnique;

        const itemsWithUUIDsUpdate = action.payload.set_attachments.update.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          updateItem: true,
        }));
        state.checkState.update = itemsWithUUIDsUpdate;

        const itemsWithUUIDsIgnore = action.payload.set_attachments.ignore.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: false,
        }));
        state.checkState.ignore = itemsWithUUIDsIgnore;
      } else if (action.payload.upload_type === UploadType['Set Serial Numbers']) {
        const itemsWithUUIDsUnique = action.payload.set_uss.unique.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: true,
        }));
        state.checkState.unique = itemsWithUUIDsUnique;

        const itemsWithUUIDsUpdate = action.payload.set_uss.update.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          updateItem: true,
        }));
        state.checkState.update = itemsWithUUIDsUpdate;

        const itemsWithUUIDsIgnore = action.payload.set_uss.ignore.map((item: ItemType) => ({
          ...item,
          id: uuidv4(),
          uniqueItem: false,
        }));
        state.checkState.ignore = itemsWithUUIDsIgnore;
      } else return;
    },
    ignoreItem: (state, action) => {
      const itemId = action.payload.id;
      state.checkState.unique = state.checkState.unique.filter(item => item.id !== itemId);

      state.checkState.ignore = [
        ...state.checkState.ignore,
        {
          ...action.payload,
          reason: 'Ignored by user',
        },
      ];
    },
    restoreItem: (state, action) => {
      const itemId = action.payload.id;
      state.checkState.ignore = state.checkState.ignore.filter(item => item.id !== itemId);
      state.checkState.unique = [...state.checkState.unique, action.payload];
    },
    restoreDuplicate: (state, action) => {
      const itemId = action.payload.id;
      state.checkState.ignore = state.checkState.ignore.filter(item => item.id !== itemId);
      state.checkState.update = [
        ...state.checkState.update,
        {
          ...action.payload,
          updated: true,
        },
      ];
    },
    addDuplicate: (state, action) => {
      state.checkState.update = state.checkState.update.map(item => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            updated: true,
          };
        }
        return item;
      });
    },
    ignoreDuplicateSetOrDevice: (state, action) => {
      const itemId = action.payload.id;
      state.checkState.update = state.checkState.update.filter(item => item.id !== itemId);

      state.checkState.ignore = [
        ...state.checkState.ignore,
        {
          ...action.payload,
          updated: false,
          updated_data: {
            ...action.payload.updated_data,
            reason: 'Duplicate ignored by user',
          },
        },
      ];
    },
    ignoreDuplicateAttachment: (state, action) => {
      const itemId = action.payload.id;
      state.checkState.update = state.checkState.update.filter(item => item.id !== itemId);

      state.checkState.ignore = [
        ...state.checkState.ignore,
        {
          ...action.payload,
          updated: false,
          reason: 'Duplicate ignored by user',
        },
      ];
    },
    approveAllDuplicates: state => {
      state.checkState.update = state.checkState.update.map(item => {
        return {
          ...item,
          updated: true,
        };
      });
    },
  },
  extraReducers: {},
});

export const { updateData, ignoreItem } = dataUploadSlice.actions;
export const selectDataUpload = (state: RootState): DataUploadSliceState => state.dataUpload;
export const selectUnique = (state: RootState): ItemType[] => state.dataUpload.checkState.unique;
export const selectIgnore = (state: RootState): ItemType[] => state.dataUpload.checkState.ignore;
export const selectUpdate = (state: RootState): ItemType[] => state.dataUpload.checkState.update;

export default dataUploadSlice;
